import { useCallback, useEffect, useState } from 'react';
import { catalogSavesStorageKey } from '../shared/catalogLookbook';

const readSavedIds = (storageKey: string): Set<string> => {
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) {
      return new Set();
    }
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? new Set(parsed.filter((id) => typeof id === 'string')) : new Set();
  } catch (error) {
    console.error('Failed to load saved catalog looks', { storageKey, error });
    return new Set();
  }
};

export const useCatalogSaves = (catalogUuid?: string) => {
  const storageKey = catalogUuid ? catalogSavesStorageKey(catalogUuid) : null;
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!storageKey) {
      setSavedIds(new Set());
      setHydrated(true);
      return;
    }
    setSavedIds(readSavedIds(storageKey));
    setHydrated(true);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || !hydrated) {
      return;
    }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(Array.from(savedIds)));
    } catch (error) {
      console.error('Failed to persist saved catalog looks', { storageKey, error });
    }
  }, [savedIds, storageKey, hydrated]);

  const toggleSave = useCallback((itemUuid?: string) => {
    if (!itemUuid) {
      return;
    }
    setSavedIds((current) => {
      const next = new Set(current);
      if (next.has(itemUuid)) {
        next.delete(itemUuid);
      } else {
        next.add(itemUuid);
      }
      return next;
    });
  }, []);

  const isSaved = useCallback((itemUuid?: string) => Boolean(itemUuid && savedIds.has(itemUuid)), [savedIds]);

  return {
    savedIds,
    toggleSave,
    isSaved,
    savedCount: savedIds.size,
  };
};
